import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";
import { EDITABLE_IMAGE_TYPES, humanFileSize } from "../../helpers/image_helpers";

const ROW = "[data-admin--checkout-form-products-target='productRow']";
const IMAGE_INPUT = "[data-admin--checkout-form-products-target='imageInput']";
const IMAGE_PREVIEW = "[data-admin--checkout-form-products-target='imagePreview']";
const JPEG_QUALITY = 0.92;
const MIN_WIDTH = 16;

export default class extends Controller {
  static targets = [
    "dialog",
    "image",
    "scaleInput",
    "scaleSlider",
    "outputInfo",
    "originalInfo",
    "arrow",
    "cropInfo",
    "applyButton",
  ];

  // Triggered by clicking the thumbnail; a persisted image's url/type data attributes live on
  // the row's preview container.
  open(event) {
    const row = event.currentTarget.closest(ROW);
    if (!row) return;

    const preview = row.querySelector(IMAGE_PREVIEW);
    const input = row.querySelector(IMAGE_INPUT);
    const file = input?.files?.[0];

    let url, contentType, filename;
    if (file) {
      contentType = file.type;
      filename = file.name;
      if (EDITABLE_IMAGE_TYPES.includes(contentType)) {
        url = this._objectUrl = URL.createObjectURL(file);
      }
    } else if (preview) {
      contentType = preview.dataset.contentType;
      filename = preview.dataset.filename;
      url = preview.dataset.imageUrl;
    }
    if (!url || !EDITABLE_IMAGE_TYPES.includes(contentType)) return;

    this._input = input;
    this._contentType = contentType;
    this._filename = filename;
    this._originalByteSize = file ? file.size : Number(preview?.dataset.byteSize) || null;
    this.outputInfoTarget.textContent = "";
    this.originalInfoTarget.textContent = "";
    this.arrowTarget.classList.add("hidden");
    this.cropInfoTarget.classList.add("hidden");
    this.scaleInputTarget.value = "100";
    this.scaleSliderTarget.value = "100";

    this.dialogTarget.showModal();
    this.imageTarget.src = url;
    this._cropper = new Cropper(this.imageTarget, {
      viewMode: 1,
      autoCropArea: 1,
      rotatable: false,
      scalable: false,
      ready: () => this._showOriginalInfo(),
      crop: () => this._cropChanged(),
    });
  }

  close() {
    this.dialogTarget.close();
  }

  cleanup() {
    this._cropper?.destroy();
    this._cropper = null;
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl);
      this._objectUrl = null;
    }
    this.imageTarget.removeAttribute("src");
    this.cropInfoTarget.classList.add("hidden");
    clearTimeout(this._estimateTimer);
  }

  scaleChanged() {
    this.scaleSliderTarget.value = this.scaleInputTarget.value || "100";
    this._refreshCropInfo();
    this._scheduleEstimate();
  }

  scaleSliderChanged() {
    this.scaleInputTarget.value = this.scaleSliderTarget.value;
    this._refreshCropInfo();
    this._scheduleEstimate();
  }

  apply() {
    if (!this._cropper || this.applyButtonTarget.disabled) return;

    const { width, height } = this._outputDimensions();
    if (!width) return;

    this.applyButtonTarget.disabled = true;
    this._renderBlob(width, height, (blob) => {
      this.applyButtonTarget.disabled = false;
      if (!blob) return;

      const file = new File([blob], this._filename || "image", { type: this._contentType });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      this._input.files = transfer.files;
      this._input.dispatchEvent(new Event("change", { bubbles: true }));
      this.close();
    });
  }

  _cropChanged() {
    if (!this._cropper) return;

    this._refreshCropInfo();
    this._scheduleEstimate();
  }

  _showOriginalInfo() {
    if (!this._cropper) return;

    const { naturalWidth, naturalHeight } = this._cropper.getImageData();
    const bytes = this._originalByteSize ? ` · ${humanFileSize(this._originalByteSize)}` : "";
    this.originalInfoTarget.textContent = `${naturalWidth}×${naturalHeight}${bytes}`;
    this.arrowTarget.classList.remove("hidden");
  }

  // Instant feedback while dragging: dimensions are cheap to compute, so the badge tracks the
  // crop box on every move; the byte-size estimate joins once the debounced render finishes.
  _refreshCropInfo() {
    if (!this._cropper) return;

    const { width, height } = this._outputDimensions();
    if (!width) return;

    const box = this._cropper.getCropBoxData();
    const info = this.cropInfoTarget;
    info.textContent = `${width}×${height}`;
    Object.assign(info.style, { left: `${box.left + 6}px`, top: `${box.top + 6}px` });
    info.classList.remove("hidden");
    this.outputInfoTarget.textContent = `${width}×${height}`;
  }

  _scheduleEstimate() {
    clearTimeout(this._estimateTimer);
    this._estimateTimer = setTimeout(() => this._updateEstimate(), 300);
  }

  _updateEstimate() {
    if (!this._cropper) return;

    const { width, height } = this._outputDimensions();
    if (!width) return;

    this._renderBlob(width, height, (blob) => {
      if (!blob) return;

      const text = `${width}×${height} · ${humanFileSize(blob.size)}`;
      this.outputInfoTarget.textContent = text;
      this.cropInfoTarget.textContent = text;
    });
  }

  _outputDimensions() {
    const crop = this._cropper.getData();
    if (!crop.width || !crop.height) return {};

    // 100% caps at the cropped region's natural size so images are never upscaled.
    const max = Math.round(crop.width);
    const percent = Math.min(Math.max(parseInt(this.scaleInputTarget.value, 10) || 100, 1), 100);
    const width = Math.min(Math.max(Math.round((crop.width * percent) / 100), MIN_WIDTH), max);
    return { width, height: Math.max(Math.round(width * (crop.height / crop.width)), 1) };
  }

  _renderBlob(width, height, callback) {
    const canvas = this._cropper.getCroppedCanvas({ width, height, imageSmoothingQuality: "high" });
    canvas.toBlob(callback, this._contentType, JPEG_QUALITY);
  }
}
